import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Wallet, Lock, User, Eye, EyeOff, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

// Users who can see dashboard and app selection (in addition to admins and users with nas_access)
const DASHBOARD_ACCESS_USERS = ['alma', 'jelyn', 'nicole', 'carla', 'kristine', 'pabs'];

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });

  // Clear any expired tokens when login page loads
  React.useEffect(() => {
    // Check if there's an old token that might be expired
    const oldToken = localStorage.getItem('token');
    if (oldToken) {
      // Clear it to prevent "Token expired" errors
      localStorage.removeItem('token');
      delete axios.defaults.headers.common['Authorization'];
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const result = await login(formData.username, formData.password);
    
    if (result.success) {
      toast.success('Login successful!');
      
      // Check if user has dashboard access
      const user = result.user;
      const usernameLower = user?.username?.toLowerCase() || '';
      const fullNameLower = user?.full_name?.toLowerCase().replace(/\s+/g, '') || '';
      
      // User gets AppSelect page if:
      // 1. They are admin, OR
      // 2. They have NAS access enabled, OR
      // 3. They are in the DASHBOARD_ACCESS_USERS list
      const hasNASAccess = user?.nas_access === true || 
        user?.nas_access === "true" || 
        user?.nas_access === 1 ||
        user?.nas_access === "1";
      
      const hasDashboardAccess = user?.role === 'admin' || 
        hasNASAccess ||
        DASHBOARD_ACCESS_USERS.some(name => usernameLower.includes(name) || fullNameLower.includes(name));
      
      // Debug log
      console.log('Login redirect decision:', {
        username: user?.username,
        role: user?.role,
        nas_access: user?.nas_access,
        hasNASAccess,
        hasDashboardAccess,
        redirectTo: hasDashboardAccess ? '/' : '/arbe/timesheet'
      });
      
      if (hasDashboardAccess) {
        navigate('/'); // Go to app selection
      } else {
        navigate('/arbe/timesheet'); // Go directly to timesheet
      }
    } else {
      toast.error(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-2xl p-8">
          {/* Logo */}
          <div className="flex justify-center mb-6">
            <div className="relative w-20 h-20">
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-blue-700 rounded-full flex items-center justify-center shadow-lg border-2 border-blue-400">
                <span className="text-white font-jakarta font-bold text-2xl tracking-tight">Mipay</span>
              </div>
              <div className="absolute -bottom-1 -right-1 w-6 h-6 bg-green-500 rounded-full border-2 border-white flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
              </div>
            </div>
          </div>

          {/* Title */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-jakarta font-bold text-slate-900 mb-2">Mipay</h1>
            <p className="text-slate-600">Subscription Management System</p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <Label htmlFor="username" className="text-slate-700">Username</Label>
              <div className="relative mt-1">
                <User className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="username"
                  type="text"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  className="pl-10"
                  placeholder="Enter username"
                  required
                  data-testid="login-username"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">Password</Label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={18} />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="pl-10 pr-10"
                  placeholder="Enter password"
                  required
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-500 hover:text-blue-600 transition-colors"
                  tabIndex={-1}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full bg-slate-900 hover:bg-slate-800"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Logging in...' : 'Login'}
            </Button>
          </form>

          {/* Customer Support Link */}
          <div className="mt-6 pt-6 border-t border-slate-200">
            <p className="text-center text-slate-600 text-sm mb-3">Are you a subscriber?</p>
            <Link to="/customer-support">
              <Button 
                variant="outline" 
                className="w-full border-blue-200 text-blue-600 hover:bg-blue-50"
                data-testid="customer-support-link"
              >
                <MessageSquare size={18} className="mr-2" />
                Customer Support Portal
              </Button>
            </Link>
          </div>

          {/* Login form ends here */}
        </div>

        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-slate-400 text-sm">Powered by Mipay © 2025</p>
        </div>
      </div>
    </div>
  );
};

export default Login;